import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { customerApi, bookingApi, invoiceApi, depositApi, portalAdminApi, formatApiError } from '../services/api';
import EditableNumber from '../components/EditableNumber';
import { 
  ArrowLeft, User, Envelope, Phone, MapPin, IdentificationCard, 
  Calendar, ShieldCheck, Receipt, Car, ArrowRight, FileArrowDown 
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import CustomerAvatar from '../components/CustomerAvatar';
import CustomerDocumentsSection from '../components/CustomerDocumentsSection';
import { LicenseClassBadges } from '../components/LicenseClassPicker';
import IdScanCard from '../components/IdScanCard';
import IdScanGallery from '../components/IdScanGallery';
import WhatsAppChatDrawer from '../components/WhatsAppChatDrawer';
import { WhatsappLogo } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const STATUS_LABELS = { confirmed: 'Bestätigt', active: 'Aktiv', completed: 'Abgeschlossen', cancelled: 'Storniert' };
const STATUS_STYLES = { confirmed: 'badge-warning', active: 'badge-success', completed: 'badge-neutral', cancelled: 'badge-error' };
const DEP_STATUS = { hinterlegt: 'Hinterlegt', 'zurückgegeben': 'Zurückgegeben' };
const INV_STATUS = { pending: 'Ausstehend', partial: 'Teilbezahlt', paid: 'Bezahlt' };

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [custRes, bookRes, depRes, invRes] = await Promise.all([
        customerApi.getOne(id),
        bookingApi.getAll({ customer_id: id }),
        depositApi.getAll({ customer_id: id }),
        invoiceApi.getAll({ customer_id: id })
      ]);
      setCustomer(custRes.data);
      setBookings(bookRes.data);
      setDeposits(depRes.data);
      setInvoices(invRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const [statementYear, setStatementYear] = useState('all');
  const [waOpen, setWaOpen] = useState(false);
  const downloadStatement = async () => {
    try {
      const params = statementYear && statementYear !== 'all' ? { year: statementYear } : {};
      const res = await customerApi.getStatementPdf(id, params);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = statementYear && statementYear !== 'all' ? `_${statementYear}` : '';
      a.download = `kontoauszug_${customer?.first_name}_${customer?.last_name}${suffix}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };


  // Ausweis nachscannen: nur leere Stammdaten ergänzen
  const handleIdScan = async (data, _photo, docType) => {
    if (!customer || !data) return;

    const isEmpty = (v) =>
      v === null || v === undefined || String(v).trim() === '';

    const patch = {};

    const pick = (field, value) => {
      if (!isEmpty(value) && isEmpty(customer[field])) {
        patch[field] = value;
      }
    };

    pick('first_name', data.first_name);
    pick('last_name', data.last_name);
    pick('date_of_birth', data.date_of_birth);
    pick('address', data.address);
    pick('postal_code', data.postal_code);
    pick('city', data.city);

    if (data.nationality && isEmpty(customer.country)) {
      patch.country = data.nationality.toLowerCase().includes('deutsch')
        ? 'Deutschland'
        : data.nationality;
    }

    pick('driver_license_number', data.driver_license_number);

    if (docType === 'fuehrerschein') {
      pick('driver_license_expiry', data.expiry_date);

      if (
        Array.isArray(data.license_classes) &&
        data.license_classes.length > 0 &&
        (!customer.driver_license_classes ||
          customer.driver_license_classes.length === 0)
      ) {
        patch.driver_license_classes = data.license_classes;
      }
    }

    if (Object.keys(patch).length > 0) {
      try {
        await customerApi.update(customer.id, patch);

        toast.success('Kundendaten aktualisiert', {
          description: `${Object.keys(patch).length} Feld(er) übernommen`,
        });
      } catch (e) {
        toast.error(formatApiError(e));
      }
    } else {
      toast.info('Ausweis gespeichert', {
        description: 'Alle Stammdaten waren bereits gepflegt',
      });
    }

    loadData();
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 w-48 skeleton rounded" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="card-swiss h-64 skeleton" />
          <div className="lg:col-span-2 card-swiss h-64 skeleton" />
        </div>
      </div>
    );
  }

  if (!customer) return null;

  const openDeposits = deposits.filter(d => d.status === 'hinterlegt');
  const returnedDeposits = deposits.filter(d => d.status === 'zurückgegeben');
  const totalOpenDeposits = openDeposits.reduce((s, d) => s + d.amount, 0);
  const totalOpenInvoices = invoices.reduce((s, i) => s + (i.total - i.paid_amount), 0);

  return (
    <div className="p-6 space-y-6" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/customers')} className="p-2">
          <ArrowLeft weight="bold" size={20} />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-[#111418]">
            {customer.first_name} {customer.last_name}
          </h1>
          <p className="text-[#525866] flex items-center gap-3 flex-wrap">
            <span>{customer.email}</span>
            <span className="text-xs px-2 py-0.5 bg-[#F1F2F4] rounded text-[#525866] flex items-center gap-1">
              KD-Nr.:
              <EditableNumber
                value={customer.customer_number}
                label="Kundennummer"
                testId="customer-number"
                small
                onSave={async (newVal) => {
                  const res = await customerApi.update(customer.id, { customer_number: newVal });
                  setCustomer(res.data);
                }}
              />
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="statement-controls">
          <select
            value={statementYear}
            onChange={e => setStatementYear(e.target.value)}
            className="px-3 py-2 border border-[#E2E4E9] rounded text-sm bg-white"
            data-testid="statement-year-select"
          >
            <option value="all">Gesamt</option>
            {(() => {
              const y = new Date().getFullYear();
              return [y, y-1, y-2, y-3, y-4].map(yr => <option key={yr} value={yr}>{yr}</option>);
            })()}
          </select>
          <Button 
            onClick={downloadStatement} 
            className="btn-primary"
            data-testid="download-statement-btn"
          >
            <FileArrowDown weight="bold" size={18} className="mr-2" />
            Kontoauszug PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => setWaOpen(true)}
            className="border-[#25D366] text-[#128C7E] hover:bg-[#E7F9EE]"
            disabled={!customer.phone}
            title={customer.phone ? 'WhatsApp Chat öffnen' : 'Kunde hat keine Telefonnummer'}
            data-testid="wa-open-btn"
          >
            <WhatsappLogo weight="fill" size={18} className="mr-2 text-[#25D366]" />
            WhatsApp
          </Button>
        </div>
        <Button
          variant="outline"
          onClick={async () => {
            try {
              const r = await portalAdminApi.sendMagicLink(customer.id);
              await navigator.clipboard.writeText(r.data.setup_link).catch(() => {});
              const emailStatus = r.data.email_sent
                ? `E-Mail an ${r.data.customer_email} versandt`
                : r.data.customer_email
                  ? 'E-Mail-Versand nicht konfiguriert – Link in Zwischenablage kopiert'
                  : 'Kunde hat keine E-Mail-Adresse – Link in Zwischenablage kopiert';
              toast.success('Portal-Zugangs-Link erstellt', {
                description: emailStatus,
                duration: 10000,
              });
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }}
          data-testid="send-portal-link-btn"
        >
          Portal-Zugang senden
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Customer Info */}
        <div className="space-y-4">
          <div className="card-swiss p-5">
            <div className="flex items-center gap-3 mb-4">
              <CustomerAvatar customer={customer} size={56} />
              <div>
                <h2 className="font-bold text-lg">{customer.first_name} {customer.last_name}</h2>
                <p className="text-xs text-[#8A94A6]">Geb. {new Date(customer.date_of_birth).toLocaleDateString('de-DE')}</p>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-[#525866]"><Envelope weight="bold" size={16} />{customer.email}</div>
              <div className="flex items-center gap-2 text-[#525866]"><Phone weight="bold" size={16} />{customer.phone}</div>
              <div className="flex items-center gap-2 text-[#525866]"><MapPin weight="bold" size={16} />{customer.address}, {customer.postal_code} {customer.city}</div>
              <div className="flex items-center gap-2 text-[#525866]"><IdentificationCard weight="bold" size={16} />{customer.driver_license_number}</div>
              <p className="text-xs text-[#8A94A6]">FS gültig bis {new Date(customer.driver_license_expiry).toLocaleDateString('de-DE')}</p>
              {customer.driver_license_classes?.length > 0 && (
                <div className="pt-1">
                  <p className="text-xs text-[#8A94A6] mb-1">Führerscheinklassen:</p>
                  <LicenseClassBadges classes={customer.driver_license_classes} size="md" />
                </div>
              )}
              {customer.last_id_scan_at && (
                <p
                  className="text-[11px] text-[#8A94A6] pt-2"
                  data-testid="last-id-scan-info"
                >
                  Letzter Ausweis-Scan: {new Date(customer.last_id_scan_at).toLocaleString('de-DE')}
                  {(customer.id_documents?.length || 0) > 0 &&
                    ` · ${customer.id_documents.length} Scan(s) archiviert`}
                </p>
              )}
            </div>
          </div>

          {/* Ausweis nachscannen */}
          <IdScanCard
            onExtracted={handleIdScan}
            customerId={customer.id}
          />

          {/* Archiv der bisherigen Ausweis-Scans */}
          <IdScanGallery
            documents={customer.id_documents}
            customerId={customer.id}
            canDelete={isAdmin}
            onChange={loadData}
          />

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="stat-card !p-4">
              <p className="text-xs text-[#8A94A6] font-bold uppercase tracking-wider">Buchungen</p>
              <p className="text-2xl font-bold">{bookings.length}</p>
            </div>
            <div className="stat-card !p-4">
              <p className="text-xs text-[#8A94A6] font-bold uppercase tracking-wider">Rechnungen</p>
              <p className="text-2xl font-bold">{invoices.length}</p>
            </div>
            <div className="stat-card !p-4">
              <p className="text-xs text-[#8A94A6] font-bold uppercase tracking-wider">Offene Kautionen</p>
              <p className="text-2xl font-bold text-[#009951]">€{totalOpenDeposits.toFixed(0)}</p>
            </div>
            <div className="stat-card !p-4">
              <p className="text-xs text-[#8A94A6] font-bold uppercase tracking-wider">Offene Beträge</p>
              <p className="text-2xl font-bold text-[#E60000]">€{totalOpenInvoices.toFixed(0)}</p>
            </div>
          </div>
        </div>

        {/* Right: History */}
        <div className="lg:col-span-2 space-y-6">
          {/* Documents / Profile */}
          <CustomerDocumentsSection
            customer={customer}
            onChanged={loadData}
            isAdmin={isAdmin}
          />

          {/* Kautionen */}
          <div className="card-swiss">
            <div className="p-4 border-b border-[#E2E4E9] flex items-center gap-2">
              <ShieldCheck weight="fill" size={20} className="text-[#009951]" />
              <h3 className="font-bold">Kautionen ({deposits.length})</h3>
              {totalOpenDeposits > 0 && (
                <span className="ml-auto badge badge-success">€{totalOpenDeposits.toFixed(2)} hinterlegt</span>
              )}
            </div>
            {deposits.length === 0 ? (
              <p className="p-4 text-sm text-[#8A94A6] text-center">Keine Kautionen</p>
            ) : (
              <div className="divide-y divide-[#E2E4E9]">
                {deposits.map(dep => (
                  <div key={dep.id} className="p-4 flex items-center justify-between" data-testid={`cust-deposit-${dep.id}`}>
                    <div>
                      <p className="font-medium text-[#111418]">{dep.description}</p>
                      <p className="text-xs text-[#8A94A6]">{new Date(dep.created_at).toLocaleDateString('de-DE')}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold text-lg">€{dep.amount.toFixed(2)}</span>
                      <span className={`badge ${dep.status === 'hinterlegt' ? 'badge-success' : 'badge-neutral'}`}>
                        {DEP_STATUS[dep.status]}
                      </span>
                      {dep.returned_at && (
                        <span className="text-xs text-[#8A94A6]">
                          Zurück: {new Date(dep.returned_at).toLocaleDateString('de-DE')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Buchungen */}
          <div className="card-swiss">
            <div className="p-4 border-b border-[#E2E4E9] flex items-center gap-2">
              <Calendar weight="fill" size={20} className="text-[#0A0A0A]" />
              <h3 className="font-bold">Buchungen ({bookings.length})</h3>
            </div>
            {bookings.length === 0 ? (
              <p className="p-4 text-sm text-[#8A94A6] text-center">Keine Buchungen</p>
            ) : (
              <div className="divide-y divide-[#E2E4E9]">
                {bookings.map(b => (
                  <Link key={b.id} to={`/bookings/${b.id}`} className="p-4 flex items-center justify-between hover:bg-[#F7F8FA] transition-colors block">
                    <div className="flex items-center gap-3">
                      <Car weight="bold" size={18} className="text-[#8A94A6]" />
                      <div>
                        <p className="font-medium text-[#111418]">{b.vehicle?.brand} {b.vehicle?.model}</p>
                        <p className="text-xs text-[#8A94A6]">{b.vehicle?.license_plate}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm">{new Date(b.start_date).toLocaleDateString('de-DE')} - {new Date(b.end_date).toLocaleDateString('de-DE')}</p>
                        <p className="text-xs text-[#8A94A6]">{b.price_info?.days} Tage · €{b.price_info?.total_price?.toFixed(2)}</p>
                      </div>
                      <span className={`badge ${STATUS_STYLES[b.status]}`}>{STATUS_LABELS[b.status]}</span>
                      <ArrowRight weight="bold" size={14} className="text-[#8A94A6]" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Rechnungen */}
          <div className="card-swiss">
            <div className="p-4 border-b border-[#E2E4E9] flex items-center gap-2">
              <Receipt weight="fill" size={20} className="text-[#FFB800]" />
              <h3 className="font-bold">Rechnungen ({invoices.length})</h3>
              {totalOpenInvoices > 0 && (
                <span className="ml-auto badge badge-error">€{totalOpenInvoices.toFixed(2)} offen</span>
              )}
            </div>
            {invoices.length === 0 ? (
              <p className="p-4 text-sm text-[#8A94A6] text-center">Keine Rechnungen</p>
            ) : (
              <div className="divide-y divide-[#E2E4E9]">
                {invoices.map(inv => (
                  <div key={inv.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-[#111418]">{inv.invoice_number}</p>
                      <p className="text-xs text-[#8A94A6]">{new Date(inv.created_at).toLocaleDateString('de-DE')}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold">€{inv.total.toFixed(2)}</p>
                        <p className="text-xs text-[#8A94A6]">Bezahlt: €{inv.paid_amount.toFixed(2)}</p>
                      </div>
                      <span className={`badge ${inv.status === 'paid' ? 'badge-success' : 'badge-warning'}`}>
                        {INV_STATUS[inv.status]}
                      </span>
                      {inv.booking_id && (
                        <Link to={`/bookings/${inv.booking_id}`}>
                          <Button variant="ghost" size="sm"><ArrowRight weight="bold" size={14} /></Button>
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <WhatsAppChatDrawer
        open={waOpen}
        onOpenChange={setWaOpen}
        customer={customer}
      />
    </div>
  );
};

export default CustomerDetail;
